/**
 * Cycle Time & Aging Analytics Page
 * Shows cycle time trends, aging analysis, and work item velocity metrics
 * Supports in-place drawer opening via ?metric=cycle_time_aging query param
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  Clock, Calendar, TrendingUp, BarChart2, Activity, 
  RefreshCw, Loader2, Filter, ArrowUp, ArrowDown,
  AlertTriangle, X, ChevronDown
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';
import { useFilters } from '../context/FilterContext';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, trend, trendUp, icon: Icon, color, onClick }) => (
  <div 
    className={`bg-white rounded-lg border p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      {Icon && (
        <div className={`p-2 rounded-lg bg-gray-100`}>
          <Icon className={`w-5 h-5 ${color || 'text-gray-500'}`} />
        </div>
      )}
    </div>
    {trend !== undefined && (
      <div className={`mt-2 flex items-center gap-1 text-sm ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
        {trendUp ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        <span>{trend}% vs last period</span>
      </div>
    )}
  </div>
);

// Aging Bucket Colors
const AGING_COLORS = {
  '0-7 days': '#22c55e',
  '8-14 days': '#84cc16',
  '15-30 days': '#f59e0b',
  '31-60 days': '#f97316',
  '>60 days': '#ef4444'
};

// Main Component
const CycleTimeAgingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { hasPermission, hasRole } = useAuth();
  const { filters: globalFilters } = useFilters();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showAgingDrawer, setShowAgingDrawer] = useState(false);
  const [selectedAgingBucket, setSelectedAgingBucket] = useState(null);
  const [agingItems, setAgingItems] = useState([]);
  
  // Filter state
  const [localFilters, setLocalFilters] = useState({
    portfolio_id: searchParams.get('portfolio_id') || '',
    program_id: searchParams.get('program_id') || '',
    project_id: searchParams.get('project_id') || '',
    team: searchParams.get('team') || '',
    type: searchParams.get('type') || '',
    date_range: searchParams.get('date_range') || 'last_90_days'
  });

  // Check for metric query param to open drawer
  useEffect(() => {
    const metric = searchParams.get('metric');
    if (metric === 'cycle_time_aging') {
      setShowAgingDrawer(true);
    }
  }, [searchParams]);

  // RBAC check
  const canAccessAnalytics = useMemo(() => {
    return hasRole('pmo_power', 'admin', 'sysadmin', 'executive') || 
           hasPermission('analytics.view');
  }, [hasRole, hasPermission]);

  // Fetch cycle time data
  const fetchData = useCallback(async () => {
    if (!canAccessAnalytics) {
      setError('no_access');
      setLoading(false);
      return;
    }

    try {
      setRefreshing(true);
      
      const params = new URLSearchParams();
      if (localFilters.portfolio_id) params.append('portfolio_id', localFilters.portfolio_id);
      if (localFilters.program_id) params.append('program_id', localFilters.program_id);
      if (localFilters.project_id) params.append('project_id', localFilters.project_id);
      if (localFilters.team) params.append('team', localFilters.team);
      if (localFilters.type) params.append('type', localFilters.type);
      params.append('date_range', localFilters.date_range);

      const response = await fetch(`${API_BASE}/analytics/v1/cycle-time?${params}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch cycle time data:', err);
      // Use mock data for demo
      setData(generateMockData());
      setError(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [localFilters, canAccessAnalytics]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Generate mock data for demo
  const generateMockData = () => {
    const weeks = 12;
    const weeklyData = Array.from({ length: weeks }, (_, i) => {
      const weekNum = weeks - i;
      return {
        week: `Week ${weekNum}`,
        cycle_time: Math.round(8 + Math.random() * 10),
        lead_time: Math.round(12 + Math.random() * 15),
        throughput: Math.round(15 + Math.random() * 25),
        wip: Math.round(20 + Math.random() * 30)
      };
    }).reverse();

    const avgCycleTime = weeklyData.reduce((sum, w) => sum + w.cycle_time, 0) / weeks;
    const avgLeadTime = weeklyData.reduce((sum, w) => sum + w.lead_time, 0) / weeks;

    return {
      kpis: {
        avg_cycle_time_days: avgCycleTime.toFixed(1),
        avg_lead_time_days: avgLeadTime.toFixed(1),
        throughput_per_week: Math.round(weeklyData.reduce((s, w) => s + w.throughput, 0) / weeks),
        wip_count: weeklyData[weeklyData.length - 1].wip,
        items_aging_over_30: Math.round(Math.random() * 15),
        items_aging_over_60: Math.round(Math.random() * 8),
        on_time_rate: (75 + Math.random() * 20).toFixed(1)
      },
      trends: weeklyData,
      aging_distribution: {
        '0-7 days': Math.round(40 + Math.random() * 20),
        '8-14 days': Math.round(25 + Math.random() * 15),
        '15-30 days': Math.round(15 + Math.random() * 10),
        '31-60 days': Math.round(8 + Math.random() * 7),
        '>60 days': Math.round(3 + Math.random() * 5)
      },
      by_type: [
        { type: 'Epic', avg_cycle: 25, count: 8 },
        { type: 'Feature', avg_cycle: 18, count: 24 },
        { type: 'Story', avg_cycle: 8, count: 85 },
        { type: 'Task', avg_cycle: 3, count: 142 },
        { type: 'Bug', avg_cycle: 5, count: 38 }
      ],
      by_priority: [
        { priority: 'Critical', avg_cycle: 4, count: 12 },
        { priority: 'High', avg_cycle: 7, count: 45 },
        { priority: 'Medium', avg_cycle: 12, count: 98 },
        { priority: 'Low', avg_cycle: 18, count: 52 }
      ]
    };
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // Open aging drawer
  const openAgingDrawer = (bucket = null) => {
    setSelectedAgingBucket(bucket);
    setShowAgingDrawer(true);
    
    // Update URL with metric param
    const newParams = new URLSearchParams(searchParams);
    newParams.set('metric', 'cycle_time_aging');
    if (bucket) newParams.set('bucket', bucket);
    setSearchParams(newParams);

    // Mock fetch aging items
    setAgingItems([
      { id: '1', title: 'API Gateway Refactor', type: 'Feature', days: 45, status: 'In Progress', project: 'Platform', assignee: 'John D.' },
      { id: '2', title: 'User Auth Flow', type: 'Story', days: 38, status: 'In Review', project: 'Mobile', assignee: 'Jane S.' },
      { id: '3', title: 'Database Migration', type: 'Epic', days: 62, status: 'Blocked', project: 'Infrastructure', assignee: 'Mike R.' },
      { id: '4', title: 'Performance Optimization', type: 'Task', days: 28, status: 'In Progress', project: 'Backend', assignee: 'Sarah L.' },
      { id: '5', title: 'Security Audit', type: 'Feature', days: 51, status: 'In Progress', project: 'Security', assignee: 'Tom H.' },
    ]);
  };

  // Close aging drawer
  const closeAgingDrawer = () => {
    setShowAgingDrawer(false);
    setSelectedAgingBucket(null);
    
    // Remove metric param from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('metric');
    newParams.delete('bucket');
    setSearchParams(newParams);
  };

  // Show RBAC error inline
  if (error === 'no_access') {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-xl mx-auto text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-amber-800 mb-2">Access Restricted</h2>
          <p className="text-amber-700">
            You don't have permission to view Cycle Time & Aging analytics.
            Please contact your administrator for access.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--clr-brand-primary)]" />
          <span className="text-gray-600">Loading cycle time analytics...</span>
        </div>
      </div>
    );
  }

  const { kpis, trends, aging_distribution, by_type, by_priority } = data || {};

  // Prepare aging pie chart data
  const agingPieData = Object.entries(aging_distribution || {}).map(([bucket, count]) => ({
    name: bucket,
    value: count,
    color: AGING_COLORS[bucket] || '#94a3b8'
  }));

  return (
    <div className="p-6" data-testid="cycle-time-aging-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Cycle Time & Aging Analytics
          </h1>
          <p className="text-gray-500 mt-1">Track work item velocity, cycle times, and aging patterns</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={localFilters.date_range}
            onChange={(e) => handleFilterChange('date_range', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
            data-testid="date-range-filter"
          >
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="last_180_days">Last 6 Months</option>
            <option value="last_365_days">Last Year</option>
          </select>
          <button
            onClick={fetchData}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Context Chips (Active Filters) */}
      {(localFilters.portfolio_id || localFilters.program_id || localFilters.project_id || localFilters.type) && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-xs text-gray-500">Active Filters:</span>
          {localFilters.portfolio_id && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs flex items-center gap-1">
              Portfolio: {localFilters.portfolio_id}
              <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('portfolio_id', '')} />
            </span>
          )}
          {localFilters.program_id && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
              Program: {localFilters.program_id}
              <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('program_id', '')} />
            </span>
          )}
          {localFilters.project_id && (
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs flex items-center gap-1">
              Project: {localFilters.project_id}
              <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('project_id', '')} />
            </span>
          )}
          {localFilters.type && (
            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs flex items-center gap-1">
              Type: {localFilters.type}
              <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('type', '')} />
            </span>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <KPICard
          title="Avg Cycle Time"
          value={`${kpis?.avg_cycle_time_days || 0} days`}
          icon={Clock}
          color="text-blue-600"
        />
        <KPICard
          title="Avg Lead Time"
          value={`${kpis?.avg_lead_time_days || 0} days`}
          icon={Calendar}
          color="text-purple-600"
        />
        <KPICard
          title="Weekly Throughput"
          value={kpis?.throughput_per_week || 0}
          subtitle="items/week"
          icon={TrendingUp}
          color="text-green-600"
        />
        <KPICard
          title="Work in Progress"
          value={kpis?.wip_count || 0}
          icon={Activity}
          color="text-orange-600"
        />
        <KPICard
          title="Aging >30 Days"
          value={kpis?.items_aging_over_30 || 0}
          icon={AlertTriangle}
          color="text-amber-600"
          onClick={() => openAgingDrawer('31-60 days')}
        />
        <KPICard
          title="Aging >60 Days"
          value={kpis?.items_aging_over_60 || 0}
          icon={AlertTriangle}
          color="text-red-600"
          onClick={() => openAgingDrawer('>60 days')}
        />
        <KPICard
          title="On-Time Rate"
          value={`${kpis?.on_time_rate || 0}%`}
          icon={BarChart2}
          color="text-teal-600"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Cycle Time Trend */}
        <div className="bg-white rounded-lg border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Cycle Time Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="cycle_time" name="Cycle Time (days)" stroke="#3b82f6" strokeWidth={2} />
              <Line type="monotone" dataKey="lead_time" name="Lead Time (days)" stroke="#8b5cf6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Aging Distribution */}
        <div className="bg-white rounded-lg border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800">Work Item Aging Distribution</h3>
            <button
              onClick={() => openAgingDrawer()}
              className="text-sm text-[var(--clr-brand-primary)] hover:underline"
              data-testid="view-aging-details-btn"
            >
              View Details
            </button>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={agingPieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
              >
                {agingPieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} cursor="pointer" onClick={() => openAgingDrawer(entry.name)} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* By Type & Priority */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cycle Time by Type */}
        <div className="bg-white rounded-lg border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Cycle Time by Work Item Type</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={by_type} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="type" type="category" width={80} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="avg_cycle" name="Avg Cycle (days)" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cycle Time by Priority */}
        <div className="bg-white rounded-lg border p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Cycle Time by Priority</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={by_priority}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="priority" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="avg_cycle" name="Avg Cycle (days)" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="count" name="Item Count" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Aging Details Drawer */}
      {showAgingDrawer && (
        <div className="fixed inset-0 z-50 flex" data-testid="aging-drawer">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-30" 
            onClick={closeAgingDrawer}
          />
          
          {/* Drawer */}
          <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Aging Work Items
                  {selectedAgingBucket && <span className="text-sm font-normal text-gray-500 ml-2">({selectedAgingBucket})</span>}
                </h2>
                <p className="text-sm text-gray-500">Items requiring attention based on aging</p>
              </div>
              <button
                onClick={closeAgingDrawer}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="close-aging-drawer-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {agingItems.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No aging items found in this bucket</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {agingItems.map(item => (
                    <div 
                      key={item.id}
                      className="p-4 bg-gray-50 rounded-lg border hover:shadow-sm transition-shadow"
                      data-testid={`aging-item-${item.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{item.title}</h4>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">{item.type}</span>
                            <span>{item.project}</span>
                            <span>{item.assignee}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`text-lg font-bold ${item.days > 60 ? 'text-red-600' : item.days > 30 ? 'text-amber-600' : 'text-gray-600'}`}>
                            {item.days} days
                          </span>
                          <p className="text-xs text-gray-400">{item.status}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={closeAgingDrawer}
                className="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CycleTimeAgingPage;
